import { useEffect, useMemo, useState } from "react";
import { UseQueryResult } from "@tanstack/react-query";

import Modal, { ModalControlProps } from "../../layouts/Modal";
import { Button } from "../base/Button";
import { TextField } from "../base/TextField";
import { useTitleConfigCategories } from "../../data/TitleConfig/useTitleConfig";

/** The fields the picker needs from a category row; both title config and price estimate rows have them. */
interface IPickableCategory {
  partCategoryId: string;
  name: string;
  isConfigured: boolean;
}

type UseCategories = (options: {
  searchTerm: string;
  configured?: boolean;
  enabled: boolean;
}) => { categories: UseQueryResult<IPickableCategory[]> };

interface CategoryPickerModalProps extends ModalControlProps {
  /**
   * Where the categories come from, and so what "configured" means. Defaults to the title
   * configuration list; the Initial Price Estimate screen passes its own.
   */
  useCategories?: UseCategories;
  title: string;
  description: React.ReactNode;
  /** True for configured categories only, false for unconfigured only, undefined for all. */
  configured?: boolean;
  /** Category to leave out, e.g. the one being copied from. */
  excludePartCategoryId?: string;
  /** Marks rows whose existing configuration this action would replace. */
  warnOnConfigured?: boolean;
  emptyText: string;
  isSaving: boolean;
  confirmLabel: (count: number) => string;
  onConfirm: (partCategoryIds: string[]) => void;
}

/**
 * Search-and-multi-select over part categories. Shared by Add (which picks unconfigured
 * categories) and Duplicate (which picks any category to copy onto), since the interaction is
 * identical and only the source list and wording differ.
 */
export default function CategoryPickerModal({
  open,
  setOpen,
  useCategories = useTitleConfigCategories,
  title,
  description,
  configured,
  excludePartCategoryId,
  warnOnConfigured = false,
  emptyText,
  isSaving,
  confirmLabel,
  onConfirm,
}: CategoryPickerModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  // Only fetch while open, and start each visit from a clean slate so a previous selection
  // cannot be submitted by accident.
  // A module-level hook passed once per mount, so the call order never changes between renders.
  const { categories } = useCategories({
    searchTerm,
    configured,
    enabled: open,
  });

  useEffect(() => {
    if (open) return;

    setSearchTerm("");
    setSelected([]);
  }, [open]);

  const rows = useMemo(
    () =>
      (categories.data ?? []).filter(
        (c) => c.partCategoryId !== excludePartCategoryId,
      ),
    [categories.data, excludePartCategoryId],
  );

  const toggle = (partCategoryId: string) =>
    setSelected((current) =>
      current.includes(partCategoryId)
        ? current.filter((id) => id !== partCategoryId)
        : [...current, partCategoryId],
    );

  const toggleAllVisible = () => {
    const visibleIds = rows.map((c) => c.partCategoryId);
    const allSelected = visibleIds.every((id) => selected.includes(id));

    setSelected((current) =>
      allSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds])),
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-lg">
      <h3 className="text-lg font-medium">{title}</h3>
      <div className="mt-1 text-sm text-gray-600">{description}</div>

      <div className="mt-4 flex items-end gap-2">
        <div className="flex-1">
          <TextField
            fullWidth
            small
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
          />
        </div>
        <Button
          size="sm"
          variant="outlined"
          onClick={toggleAllVisible}
          disabled={rows.length === 0}
        >
          Select all
        </Button>
      </div>

      <ul className="mt-2 max-h-72 divide-y divide-gray-100 overflow-y-auto rounded border border-gray-200">
        {categories.isLoading && (
          <li className="px-3 py-4 text-sm text-gray-500">Loading…</li>
        )}

        {/* Never claim the list is genuinely empty when the request failed. */}
        {!categories.isLoading && categories.isError && (
          <li className="px-3 py-4">
            <p className="text-sm font-medium text-red-800">
              Could not load categories
            </p>
            <p className="mt-1 text-xs text-red-700">
              {(categories.error as Error)?.message}
            </p>
          </li>
        )}

        {!categories.isLoading && !categories.isError && rows.length === 0 && (
          <li className="px-3 py-4 text-sm text-gray-500">{emptyText}</li>
        )}

        {rows.map((category) => (
          <li key={category.partCategoryId}>
            <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-600"
                checked={selected.includes(category.partCategoryId)}
                onChange={() => toggle(category.partCategoryId)}
              />
              <span className="flex-1 truncate">{category.name}</span>
              {warnOnConfigured && category.isConfigured && (
                <span className="shrink-0 text-[11px] text-amber-700">
                  will be overwritten
                </span>
              )}
            </label>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
          {selected.length} selected
        </span>
        <div className="flex gap-2">
          <Button variant="outlined" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selected)}
            disabled={selected.length === 0 || isSaving}
          >
            {isSaving ? "Working…" : confirmLabel(selected.length)}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
